import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAltHotkeys, type AltHotkeyBindings } from '../../../hooks/useAltHotkeys';

export interface TabDef {
  id: string;
  label: string;
  /** Single-letter hotkey, displayed as `[O]` to the left of the label and
   *  bound to Alt+letter (see the keydown effect below). */
  hotkey?: string;
  /** Optional badge value (e.g. unread count). Falsy values render no badge. */
  badge?: string | number;
}

export interface TabsProps {
  tabs: ReadonlyArray<TabDef>;
  activeId: string;
  onChange: (id: string) => void;
  /** Content pinned to the far right of the tab row (e.g. the Follow Up
   *  bookmark). Rendered OUTSIDE the scrolling tablist, so a long tab list
   *  scrolls underneath it instead of pushing it off screen, and screen
   *  readers do not announce it as a tab. */
  rightSlot?: React.ReactNode;
}

/**
 * Canonical 9-tab list for the Pro 360 Order Detail page.
 * Trouble Tickets is intentionally omitted — it now lives as a sub-tab inside
 * OrderForm's Comments & Status panel.
 *
 * The hotkey letters are V1's, so Alt+letter muscle memory carries over: V1
 * stamps `accesskey` on each tab button in `lib/lib_editrecord.pl` (~line 589),
 * hard-coding Addl Info=n, Addl Cost=v, Invoicing=x, Docs=z, Notes=g, History=h
 * and drawing the rest from the `$editrecord_accesskeys` pool in `defs.pl`
 * (which deliberately excludes a-f). Keep these in sync with V1 — in particular
 * Docs stays Z, not D, because Chrome owns Alt+D (address bar) on Windows.
 */
export const PRO_TABS: ReadonlyArray<TabDef> = [
  { id: 'order-info', label: 'Order Info', hotkey: 'O' },
  { id: 'addl-info', label: 'Addl Info', hotkey: 'N' },
  { id: 'tracing', label: 'Tracing', hotkey: 'T' },
  { id: 'whse', label: 'Whse', hotkey: 'W' },
  { id: 'addl-cost', label: 'Addl Cost', hotkey: 'V' },
  { id: 'invoicing', label: 'Invoicing', hotkey: 'X' },
  { id: 'docs', label: 'Docs', hotkey: 'Z' },
  { id: 'notes', label: 'Notes', hotkey: 'G' },
  { id: 'history', label: 'History', hotkey: 'H' },
];

/** Set of valid tab ids; used by ProPage to validate the URL hash route. */
export const PRO_TAB_IDS: ReadonlySet<string> = new Set(PRO_TABS.map((t) => t.id));

/**
 * Tabs whose edits flow through `useOrderEditing.editedOrderFields` (i.e.
 * V1's /saveorderdetails). The Pro footer "Save Changes" button is shown on
 * these tabs. The Invoicing tab has its own dedicated save button because it
 * targets a different backend endpoint (POST /orders/{n}/invoice-header).
 */
export const PRO_EDITABLE_TABS: ReadonlySet<string> = new Set([
  'order-info',
  'addl-info',
  'tracing',
]);

const Tabs: React.FC<TabsProps> = ({ tabs, activeId, onChange, rightSlot }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkScroll) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      ro?.disconnect();
    };
  }, [checkScroll]);

  // Tab hotkeys: the `[Z]`-style hint on each tab is bound to Alt+letter, the
  // same chord V1's `accesskey` tabs answer to. Only VISIBLE tabs bind, so e.g.
  // Alt+W is inert on a movetype where Whse is hidden. The Pro footer's Save /
  // Close chords ride the same hook.
  const hotkeys = useMemo(() => {
    const map: AltHotkeyBindings = {};
    for (const t of tabs) {
      if (t.hotkey) map[t.hotkey.toUpperCase()] = () => onChange(t.id);
    }
    return map;
  }, [tabs, onChange]);
  useAltHotkeys(hotkeys, scrollRef);

  const scrollByDir = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * 160, behavior: 'smooth' });
  };

  return (
    <div className="bg-panel border-b border-slate-200 dark:border-gray-700 flex items-stretch">
      {/* The scroller and its edge affordances share one positioning context so
          the right-hand gradient sits over the last tab, never over rightSlot. */}
      <div className="relative flex-1 min-w-0">
      {canScrollLeft && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          onClick={() => scrollByDir(-1)}
          className="absolute left-0 top-0 bottom-0 w-8 z-10 flex items-center justify-center cursor-pointer bg-gradient-to-r from-white via-white/80 to-transparent dark:from-gray-800 dark:via-gray-800/80"
        >
          <span className="text-sm text-slate-500 dark:text-gray-400 font-bold">‹</span>
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          onClick={() => scrollByDir(1)}
          className="absolute right-0 top-0 bottom-0 w-8 z-10 flex items-center justify-center cursor-pointer bg-gradient-to-l from-white via-white/80 to-transparent dark:from-gray-800 dark:via-gray-800/80"
        >
          <span className="text-sm text-slate-500 dark:text-gray-400 font-bold">›</span>
        </button>
      )}
      <div
        ref={scrollRef}
        role="tablist"
        className="flex flex-nowrap items-end gap-0.5 px-4 pt-2 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((t) => {
          const isActive = activeId === t.id;
          return (
            <button
              type="button"
              role="tab"
              key={t.id}
              aria-current={isActive ? 'page' : undefined}
              aria-selected={isActive}
              onClick={() => onChange(t.id)}
              className={
                'relative -mb-px px-4 py-2 text-[0.78125rem] whitespace-nowrap inline-flex items-center gap-1.5 flex-shrink-0 ' +
                'rounded-t-md border border-slate-300 dark:border-gray-600 transition-colors ' +
                (isActive
                  ? 'bg-slate-100 border-b-slate-100 text-slate-900 dark:bg-gray-900 dark:border-b-gray-900 dark:text-gray-100 font-semibold'
                  : 'bg-slate-200/70 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:bg-gray-700/70 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 font-medium')
              }
            >
              {t.hotkey && (
                <span className="text-[0.625rem] text-slate-400 dark:text-gray-500 font-medium">[{t.hotkey}]</span>
              )}
              <span>{t.label}</span>
              {t.badge != null && t.badge !== '' && t.badge !== 0 && (
                <span className="ml-0.5 text-[0.625rem] bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-semibold">
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      </div>
      {rightSlot && (
        <div
          data-testid="tabs-right-slot"
          className="flex-shrink-0 flex items-center pr-4 pl-2 pt-2"
        >
          {rightSlot}
        </div>
      )}
    </div>
  );
};

export default Tabs;
